/**
 * Servicio para obtener el token WebRTC de Telnyx desde el backend.
 * El token permite al agente registrarse como cliente SIP en el navegador
 * y originar llamadas directamente con micrófono.
 *
 * Estrategia de caché:
 * - El token de Telnyx es válido 3600s (1h).
 * - Se cachea en memoria del módulo para que llamadas sucesivas sean instantáneas.
 * - Se refresca automáticamente 5 min antes de expirar.
 * - prefetchTelnyxWebRTCToken() permite pre-calentar el token en segundo plano
 *   cuando el agente abre el marcador, antes de que pulse "Llamar".
 */

import { api } from './api';
import { logger } from '@/utils/logger';

export interface TelnyxWebRTCTokenResponse {
  token: string;
  expires_in: number;
  credential_id: string;
  /** Número E.164 para callerIdNumber en TelnyxRTC.newCall() (ej: "+34923720708"). */
  caller_id_number?: string | null;
}

// ─── Caché de token ───────────────────────────────────────────────────────────

/** Margen de seguridad: refrescar el token 5 minutos antes de que expire */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

let _cachedToken: {
  data: TelnyxWebRTCTokenResponse;
  /** Timestamp ms en el que expira */
  expiresAt: number;
} | null = null;

/** Promise en vuelo para no lanzar dos fetches simultáneos */
let _inflightFetch: Promise<TelnyxWebRTCTokenResponse> | null = null;

function _isCacheValid(): boolean {
  return (
    _cachedToken !== null &&
    Date.now() < _cachedToken.expiresAt - REFRESH_MARGIN_MS
  );
}

async function _doFetch(): Promise<TelnyxWebRTCTokenResponse> {
  const { data } = await api.get<TelnyxWebRTCTokenResponse>(
    '/crm/telnyx/webrtc-token',
    {
      // Timeout extendido para acomodar cold start de Render (puede tardar ~15-30s)
      timeout: 65_000,
    },
  );

  _cachedToken = {
    data,
    expiresAt: Date.now() + data.expires_in * 1000,
  };

  const remainSec = Math.round(data.expires_in);
  logger.info(`[TelnyxToken] Token obtenido y cacheado. Válido ${remainSec}s (hasta ${new Date(_cachedToken.expiresAt).toLocaleTimeString()})`);

  return data;
}

// ─── API pública ──────────────────────────────────────────────────────────────

/**
 * Obtiene el token WebRTC de Telnyx.
 *
 * - Si hay un token válido en caché → devuelve instantáneamente (< 1ms).
 * - Si ya hay un fetch en vuelo → espera al mismo (no lanza duplicado).
 * - Si no hay caché ni fetch activo → lanza request al backend.
 *
 * Con retry automático si el primer intento da timeout (cold start de Render).
 */
export async function fetchTelnyxWebRTCToken(): Promise<TelnyxWebRTCTokenResponse> {
  // Hit de caché: respuesta instantánea
  if (_isCacheValid()) {
    const remainSec = Math.round((_cachedToken!.expiresAt - Date.now()) / 1000);
    logger.info(`[TelnyxToken] Usando token cacheado (caduca en ${remainSec}s)`);
    return _cachedToken!.data;
  }

  // Deduplicar: si ya hay un fetch en curso, esperarlo
  if (_inflightFetch) {
    logger.info('[TelnyxToken] Fetch en vuelo, esperando resultado…');
    return _inflightFetch;
  }

  // Lanzar fetch con retry para cold start
  _inflightFetch = (async () => {
    try {
      return await _doFetch();
    } catch (err: any) {
      const isTimeout =
        err?.code === 'ECONNABORTED' ||
        String(err?.message ?? '').includes('timeout');

      if (!isTimeout) throw err;

      // Cold start de Render: esperar 2s y reintentar
      logger.warn('[TelnyxToken] Timeout (posible cold start), reintentando en 2s…');
      await new Promise<void>((r) => setTimeout(r, 2000));
      return await _doFetch();
    } finally {
      _inflightFetch = null;
    }
  })();

  return _inflightFetch;
}

/**
 * Pre-carga el token en segundo plano (sin await, sin lanzar errores).
 * Llamar cuando el agente abre el marcador para que el token esté listo
 * antes de que pulse "Llamar".
 */
export function prefetchTelnyxWebRTCToken(): void {
  if (_isCacheValid() || _inflightFetch) return; // ya está listo o en vuelo
  fetchTelnyxWebRTCToken().catch((err) => {
    logger.warn('[TelnyxToken] Prefetch fallido (se reintentará al marcar):', err?.message);
  });
}

/**
 * Invalida el caché manualmente (útil si el token devuelve 401).
 */
export function invalidateTelnyxTokenCache(): void {
  _cachedToken = null;
  _inflightFetch = null;
  logger.info('[TelnyxToken] Caché invalidado manualmente');
}
